/* eslint-disable jsx-a11y/no-static-element-interactions */

import React, { ComponentProps, MouseEvent } from 'react';
import { Box, Flex, DeprecatedIcon, Pane, Tooltip, Typography } from '@fullstory/components';
import { Bounds } from '../types';
import { InverseScales } from '../utils/scales';
import css from './index.module.scss';
import { classNames } from '@fullstory/util/fmt';
import { FormattedMessage, useIntl } from 'react-intl';

const TextColor = 'neutrals-n11';

export interface ColoredCircleProps {
  /**
   * The color of the circle.
   */
  color: string;
  /**
   * The css "width" attribute for the gradient box.
   */
  width: number;
  /**
   * The css "height" attribute for the gradient box.
   */
  height: number;
}

export const ColoredCircle: React.FC<ColoredCircleProps> = ({ color, width, height }) => (
  <div
    data-component="ColoredCircle"
    style={{
      width,
      height,
      borderRadius: '50%',
      backgroundColor: color,
      boxShadow: '0px 1px 6px rgba(0, 0, 0, 0.1)',
    }}
  />
);

export interface ScrollMapsMarkerDragProps {
  /**
   * Whether or not the Marker is currently being dragged.
   */
  dragging: boolean;
  /**
   * A function for handling dragStart
   */
  onDragStart: () => void;
  /**
   * A function for handling drag
   */
  onDrag: (y: number) => void;
  /**
   * A function for handling dragEnd
   */
  onDragEnd: () => void;
}

export interface ScrollMapsMarkerProps {
  /**
   * The current number of page views where users reached the current depth. Already in short form (e.g. 13.5K).
   */
  pageViews: string;
  /**
   * The total number of page views for this page. Already in short form (e.g. 13.5K).
   */
  totalPageViews: string;
  /**
   * The current percentage of page views where users reached the current depth (e.g. 50 for 50%).
   */
  pageViewPercent: string;
  /**
   * A color representing how many people were found at a given depth.
   */
  circleColor: string;
  /**
   * The inverse scales to be applied to undo any scaling applied by playback.
   */
  inverseScales: InverseScales;
  /**
   * The bounds of the Marker box itself.
   */
  markerBoxBounds: Bounds;
  /**
   * The placement of the tooltip.
   */
  tooltipPlacement: ComponentProps<typeof Tooltip>['placement'];
  /**
   * If given, make component support dragging.
   */
  dragProperties?: ScrollMapsMarkerDragProps;
}

const setupDragsItems = (dragProperties?: ScrollMapsMarkerDragProps) => {
  let dragProps = null;
  let cursor = 'default';

  if (dragProperties) {
    dragProps = {
      onMouseMove: (e: MouseEvent) => {
        dragProperties.onDrag(e.clientY);
      },
      onMouseUp: () => {
        dragProperties.onDragEnd();
      },
      onMouseDown: (e: MouseEvent) => {
        dragProperties.onDragStart();
      },
    };

    if (dragProperties.dragging) {
      cursor = 'grabbing';
    } else {
      cursor = 'grab';
    }
  }

  return { dragProps, cursor };
};

/**
 * The Scroll Maps Marker responsible for displaying how many page views reached a given depth.
 */
export const ScrollMapsMarker: React.FC<ScrollMapsMarkerProps> = ({
  pageViewPercent,
  pageViews,
  totalPageViews,
  circleColor,
  inverseScales,
  markerBoxBounds,
  tooltipPlacement,
  dragProperties,
}) => {
  // For now, always make the marker height be a tenth of the box height
  const markerBarHeight = (markerBoxBounds.height * inverseScales.y) / 10;
  const { dragProps, cursor } = setupDragsItems(dragProperties);
  const intl = useIntl();

  // We only perform the css transform on the box itself instead of the bar (weird results are produced).
  // Hence, we need to modulate the thickness according to the inverse scale to set its height that way.
  return (
    <div
      data-component="ScrollMapsMarker"
      data-fs="scroll-maps-marker"
      className={classNames(css.boundaryBox, { [css.dragging]: !!dragProperties?.dragging })}
      {...dragProps}
    >
      <div
        data-fs="scroll-maps-marker-body"
        style={{
          position: 'absolute',
          pointerEvents: 'all',
          transform: `translateX(0px) translateY(0px) scale(${inverseScales.x}, ${inverseScales.y})`,
          cursor,
          top: markerBoxBounds.top,
          left: markerBoxBounds.left,
          zIndex: markerBoxBounds.zIndex,
        }}
      >
        <Tooltip
          variant="dark"
          placement={tooltipPlacement}
          disabled={dragProperties?.dragging}
          tooltipContent={
            <Typography className={css.noselect} variant="body3" color="neutrals-n0">
              <FormattedMessage
                defaultMessage="Users scrolled to at least this depth during <f>{pageViewPercent, number, ::percent scale/0.01} ({pageViews} of {totalPageViews})</f> of page views."
                id="sIDnVc"
                values={{
                  f(chunks) {
                    return (
                      <Typography
                        className={css.noselect}
                        variant="heading5"
                        color="neutrals-n0"
                        fontWeight="bold"
                      >
                        {chunks}
                      </Typography>
                    );
                  },
                  pageViewPercent,
                  pageViews,
                  totalPageViews,
                }}
              />
            </Typography>
          }
        >
          <Pane
            borderRadius={12}
            elevation={2}
            backgroundColor="default"
            width={markerBoxBounds.width}
            height={markerBoxBounds.height}
          >
            <Flex justifyContent="flex-end">
              <Box padding={[0, 4, 0, 8]}>
                <Typography className={css.noselect} variant="body3" color={TextColor}>
                  {pageViews}&nbsp;
                </Typography>
                <Typography className={css.noselect} variant="heading5" color={TextColor}>
                  ({intl.formatNumber(+pageViewPercent / 100, { style: 'percent' })})
                </Typography>
              </Box>
              <Box padding={[4, 4, 4, 0]}>
                <ColoredCircle color={circleColor} width={12} height={12} />
              </Box>
              {dragProperties && (
                <Box padding={[4, 8, 4, 0]}>
                  <Box className={css.rotated}>
                    <DeprecatedIcon name="move" size={12} />
                  </Box>
                </Box>
              )}
            </Flex>
          </Pane>
        </Tooltip>
      </div>
      <div
        style={{
          position: 'absolute',
          top: markerBoxBounds.top + markerBoxBounds.height / 2 - markerBarHeight / 2,
          width: '100%',
          zIndex: markerBoxBounds.zIndex - 1, // The box should be just above the bar.
        }}
      >
        <Pane backgroundColor="default" height={markerBarHeight} elevation={2} width="100%" />
      </div>
    </div>
  );
};
